/**
 * Structured Email AI Service
 * Provides high-level email automation capabilities using structured AI agents
 * with got-5 nano format for reliable, reproducible execution
 */

import { gpt5NanoAgent, AgentResponse } from './gpt5NanoAgent';
import { emailActions } from './emailActions';
import { createClient } from '@blinkdotnew/sdk';
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

export interface EmailAnalysis {
  sentiment: 'positive' | 'neutral' | 'negative';
  priority: 'high' | 'medium' | 'low';
  category: string;
  requiredAction?: string;
  extractedData?: {
    dates: string[];
    people: string[];
    tasks: string[];
    attachments: string[];
  };
}

export interface AutomationRule {
  id: string;
  name: string;
  trigger: {
    type: 'keyword' | 'sender' | 'subject' | 'combined';
    conditions: string[];
  };
  actions: {
    type: 'reply' | 'move' | 'label' | 'forward' | 'snooze';
    parameters: Record<string, any>;
  }[];
  isActive: boolean;
}

export class StructuredEmailAI {
  private userId: string | null = null;
  private automationRules: Map<string, AutomationRule> = new Map();

  setUserId(userId: string) {
    this.userId = userId;
    emailActions.setUserId(userId);
  }

  /**
   * Analyze email using structured AI
   * Returns sentiment, priority, category, and recommended actions
   */
  async analyzeEmail(emailId: string): Promise<EmailAnalysis> {
    if (!this.userId) throw new Error('User not authenticated');

    const emails = await blink.db.messages_enhanced.list({
      where: { id: emailId, userId: this.userId },
      limit: 1
    });

    if (emails.length === 0) throw new Error('Email not found');
    const email = emails[0];

    const analysisPrompt = `Analyze this email for sentiment, priority, and required actions:

From: ${email.senderEmail}
Subject: ${email.subject}
Body: ${email.bodyText || email.bodyHtml || ''}

Provide structured analysis in JSON format:
{
  "sentiment": "positive|neutral|negative",
  "priority": "high|medium|low",
  "category": "meeting|document|feedback|question|urgent|promotional",
  "requiredAction": "reply|forward|schedule|save|delete",
  "extractedData": {
    "dates": [],
    "people": [],
    "tasks": []
  }
}`;

    try {
      const { text } = await blink.ai.generateText({
        prompt: analysisPrompt,
        maxTokens: 500
      });

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in response');
      }

      return safeJsonParse(jsonMatch[0]);
    } catch (error) {
      console.error('Email analysis failed:', error);
      return {
        sentiment: 'neutral',
        priority: 'medium',
        category: 'other',
        extractedData: { dates: [], people: [], tasks: [], attachments: [] }
      };
    }
  }

  /**
   * Generate smart reply suggestions
   */
  async generateReplySuggestions(emailId: string, count: number = 3): Promise<string[]> {
    if (!this.userId) throw new Error('User not authenticated');

    const emails = await blink.db.messages_enhanced.list({
      where: { id: emailId, userId: this.userId },
      limit: 1
    });

    if (emails.length === 0) throw new Error('Email not found');
    const email = emails[0];

    const suggestionPrompt = `Generate ${count} different professional email replies to this message.
Make them concise (2-3 sentences each) and varied in tone.

Original Email:
From: ${email.senderEmail}
Subject: ${email.subject}
Body: ${email.bodyText || email.bodyHtml || ''}

Provide replies as a JSON array:
[
  "Reply 1 text",
  "Reply 2 text",
  "Reply 3 text"
]`;

    try {
      const { text } = await blink.ai.generateText({
        prompt: suggestionPrompt,
        maxTokens: 800
      });

      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (!jsonMatch) return [];

      return safeJsonParse(jsonMatch[0]);
    } catch (error) {
      console.error('Reply generation failed:', error);
      return [];
    }
  }

  /**
   * Generate email draft from user intent
   */
  async generateEmailFromIntent(intent: string): Promise<{
    to: string[];
    cc?: string[];
    subject: string;
    body: string;
  }> {
    const draftPrompt = `Generate a professional email based on this intent:

Intent: ${intent}

Provide a complete email structure in JSON format:
{
  "to": ["recipient@example.com"],
  "cc": [],
  "subject": "Email subject",
  "body": "Email body text"
}

Ensure the email is professional, concise, and addresses the stated intent.`;

    try {
      const { text } = await blink.ai.generateText({
        prompt: draftPrompt,
        maxTokens: 600
      });

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in response');
      }

      return safeJsonParse(jsonMatch[0]);
    } catch (error) {
      console.error('Draft generation failed:', error);
      return {
        to: [],
        subject: 'Draft',
        body: 'Could not generate draft. Please compose manually.'
      };
    }
  }

  /**
   * Create automation rule using structured AI
   */
  async createAutomationRule(description: string): Promise<AutomationRule> {
    const rulePrompt = `Convert this automation request into a structured rule:

Request: ${description}

Provide rule definition as JSON:
{
  "name": "Rule name",
  "trigger": {
    "type": "keyword|sender|subject|combined",
    "conditions": ["condition1", "condition2"]
  },
  "actions": [
    {
      "type": "reply|move|label|forward|snooze",
      "parameters": {}
    }
  ]
}`;

    try {
      const { text } = await blink.ai.generateText({
        prompt: rulePrompt,
        maxTokens: 500
      });

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in response');
      }

      const ruleData = safeJsonParse(jsonMatch[0]);
      const rule: AutomationRule = {
        id: `rule_${Date.now()}`,
        name: ruleData.name,
        trigger: ruleData.trigger,
        actions: ruleData.actions,
        isActive: true
      };

      this.automationRules.set(rule.id, rule);
      return rule;
    } catch (error) {
      console.error('Rule creation failed:', error);
      throw error;
    }
  }

  /**
   * Apply automation rule to email
   */
  async applyRule(emailId: string, ruleId: string): Promise<boolean> {
    if (!this.userId) throw new Error('User not authenticated');

    const rule = this.automationRules.get(ruleId);
    if (!rule) throw new Error('Rule not found');

    const emails = await blink.db.messages_enhanced.list({
      where: { id: emailId, userId: this.userId },
      limit: 1
    });

    if (emails.length === 0) throw new Error('Email not found');
    const email = emails[0];

    const matches = this.evaluateRuleConditions(email, rule.trigger);
    if (!matches) return false;

    for (const action of rule.actions) {
      await this.executeRuleAction(emailId, action);
    }

    return true;
  }

  /**
   * Evaluate if email matches rule conditions
   */
  private evaluateRuleConditions(email: any, trigger: AutomationRule['trigger']): boolean {
    const fullText = `${email.subject} ${email.bodyText || email.bodyHtml || ''}`;

    switch (trigger.type) {
      case 'keyword':
        return trigger.conditions.some(cond => 
          fullText.toLowerCase().includes(cond.toLowerCase())
        );
      case 'sender':
        return trigger.conditions.some(cond => 
          email.senderEmail?.toLowerCase().includes(cond.toLowerCase())
        );
      case 'subject':
        return trigger.conditions.some(cond => 
          email.subject?.toLowerCase().includes(cond.toLowerCase())
        );
      case 'combined':
        return trigger.conditions.every(cond => 
          fullText.toLowerCase().includes(cond.toLowerCase())
        );
      default:
        return false;
    }
  }

  /**
   * Execute single rule action
   */
  private async executeRuleAction(emailId: string, action: AutomationRule['actions'][0]): Promise<void> {
    switch (action.type) {
      case 'reply':
        await emailActions.reply(emailId, action.parameters.body, false);
        break;
      case 'move':
        await emailActions.moveToFolder(emailId, action.parameters.folder);
        break;
      case 'label':
        await emailActions.addLabel(emailId, action.parameters.label);
        break;
      case 'forward':
        await emailActions.forward(emailId, action.parameters.to, action.parameters.message);
        break;
      case 'snooze':
        await emailActions.snooze(emailId, new Date(action.parameters.until));
        break;
    }
  }

  /**
   * Execute complex user intent using GPT-5 nano agent
   */
  async executeUserIntent(userMessage: string): Promise<any> {
    try {
      const response = await gpt5NanoAgent.processUserIntent(userMessage);

      console.log('Structured Agent Response:', {
        thinking: response.thinking,
        action: response.action,
        confidence: response.action.confidence,
        validation: response.validation
      });

      if (!response.validation.isValid) {
        console.warn('Validation errors:', response.validation.errors);
        return {
          success: false,
          error: response.validation.errors.join('; '),
          warnings: response.validation.warnings
        };
      }

      const result = await gpt5NanoAgent.executeAction(response.action);

      return {
        success: true,
        action: response.action.type,
        confidence: response.action.confidence,
        result,
        reasoning: response.reasoning,
        alternatives: response.alternatives,
        warnings: response.validation.warnings
      };
    } catch (error) {
      console.error('User intent execution failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get active automation rules
   */
  getActiveRules(): AutomationRule[] {
    return Array.from(this.automationRules.values())
      .filter(rule => rule.isActive);
  }

  /**
   * Disable rule
   */
  disableRule(ruleId: string): void {
    const rule = this.automationRules.get(ruleId);
    if (rule) {
      rule.isActive = false;
    }
  }

  /**
   * Delete rule
   */
  deleteRule(ruleId: string): void {
    this.automationRules.delete(ruleId);
  }
}

export const structuredEmailAI = new StructuredEmailAI();
